import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { useFirebaseImage, ImageSize, getImagePathWithSize } from '../Helpers/ImageLoader';
import { coverMarkerPosition, containMarkerPosition } from './markerGeometry';
import './MultiMarkerImage.css';

/** A single marker to overlay on the image, in the photo's original pixel space. */
export interface ImageMarker {
    key: string;
    x: number;
    y: number;
    /** Natural width/height of the photo this marker's coordinates were recorded against. */
    width: number;
    height: number;
    label: string;
}

interface MultiMarkerImageProps {
    storagePath: string;
    imageSize: ImageSize;
    /** 'cover' for fixed-size thumbnails, 'contain' for the enlarged stage. */
    fit: 'cover' | 'contain';
    markers: ImageMarker[];
    /** When true, markers are focusable buttons that fire onMarkerClick. Otherwise decorative. */
    interactive?: boolean;
    activeKey?: string | null;
    onMarkerClick?: (key: string) => void;
    /** Click on the image itself (used by the thumbnail tile to select the reference photo). */
    onImageClick?: () => void;
    alt?: string;
}

/**
 * Renders a Firebase image with multiple markers positioned from pixel
 * coordinates. Each marker scales by its OWN recorded photo dimensions, so a
 * single divergent record can only misplace its own marker — not the whole set.
 */
const MultiMarkerImage: React.FC<MultiMarkerImageProps> = ({
    storagePath,
    imageSize,
    fit,
    markers,
    interactive = false,
    activeKey = null,
    onMarkerClick,
    onImageClick,
    alt = ''
}) => {
    const path = getImagePathWithSize(storagePath, imageSize);
    const { src, isLoading, hasError } = useFirebaseImage(path);
    const containerRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const [box, setBox] = useState({ width: 0, height: 0 });

    const measure = useCallback(() => {
        if (fit === 'cover') {
            if (containerRef.current) {
                setBox({ width: containerRef.current.offsetWidth, height: containerRef.current.offsetHeight });
            }
        } else if (imgRef.current) {
            setBox({ width: imgRef.current.clientWidth, height: imgRef.current.clientHeight });
        }
    }, [fit]);

    useEffect(() => {
        measure();
        let ro: ResizeObserver | undefined;
        if (fit === 'cover' && containerRef.current) {
            ro = new ResizeObserver(measure);
            ro.observe(containerRef.current);
        }
        window.addEventListener('resize', measure);
        return () => {
            ro?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [fit, src, measure]);

    const positionFor = (marker: ImageMarker) =>
        fit === 'cover'
            ? coverMarkerPosition(box, { width: marker.width, height: marker.height }, marker)
            : containMarkerPosition(box.width, marker.width, marker);

    const renderMarker = (marker: ImageMarker) => {
        const offset = positionFor(marker);
        if (!offset) return null;
        const style: React.CSSProperties = { left: `${offset.left}px`, top: `${offset.top}px` };
        const isActive = marker.key === activeKey;
        const className = `multi-marker-dot${interactive ? ' interactive' : ''}${isActive ? ' active' : ''}`;

        if (interactive) {
            return (
                <button
                    key={marker.key}
                    type="button"
                    className={className}
                    style={style}
                    title={marker.label}
                    aria-label={marker.label}
                    aria-pressed={isActive}
                    onClick={(e) => {
                        e.stopPropagation();
                        onMarkerClick?.(marker.key);
                    }}
                />
            );
        }
        return <span key={marker.key} className={className} style={style} aria-hidden="true" />;
    };

    const stateContent = isLoading ? (
        <div className="multi-marker-image-state">
            <Loader2 size={fit === 'cover' ? 16 : 28} className="multi-marker-spinner" />
        </div>
    ) : hasError || !src ? (
        <div className="multi-marker-image-state multi-marker-image-error">
            {fit === 'cover' ? 'Fel' : 'Kunde inte ladda bild'}
        </div>
    ) : null;

    // Cover: fixed-size tile; the image fills it and markers overlay the tile box.
    if (fit === 'cover') {
        return (
            <div
                ref={containerRef}
                className={`multi-marker-image multi-marker-cover${onImageClick ? ' clickable' : ''}`}
                onClick={onImageClick}
                title={alt}
            >
                {stateContent}
                {src && !isLoading && !hasError && (
                    <>
                        <img src={src} alt={alt} draggable={false} />
                        {markers.map(renderMarker)}
                    </>
                )}
            </div>
        );
    }

    // Contain: the stage wraps the image tightly so markers overlay the rendered image.
    return (
        <div ref={containerRef} className="multi-marker-image multi-marker-contain">
            {stateContent}
            {src && !isLoading && !hasError && (
                <div className="multi-marker-stage">
                    <img
                        ref={imgRef}
                        src={src}
                        alt={alt}
                        draggable={false}
                        onLoad={measure}
                    />
                    {box.width > 0 && markers.map(renderMarker)}
                </div>
            )}
        </div>
    );
};

export default MultiMarkerImage;
